import {
	Box,
	Button,
	ClickAwayListener,
	Paper,
	Popper,
	SvgIcon,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { MutableRefObject, useEffect, useState } from "react";

import { ReactComponent as AttentionIcon } from "@client/components/reusable/SvgIcons/library/attention_icon.svg";
import { ReactComponent as ChevronLeftIcon } from "@client/components/reusable/SvgIcons/library/chevron-left.svg";

const LeftArrowIcon = () => (
	<SvgIcon
		component={ChevronLeftIcon}
		inheritViewBox
		sx={{ width: 18, height: 18 }}
	/>
);
const RightArrowIcon = () => (
	<SvgIcon
		component={ChevronLeftIcon}
		inheritViewBox
		sx={{ width: 18, height: 18, transform: "rotate(180deg)" }}
	/>
);

export const ScheduledDatePicker: React.FC<{
	anchorRef: MutableRefObject<HTMLDivElement | null>;
	open: boolean;
	setOpen: (open: boolean) => void;
	setParentDate: (date: Dayjs | null) => void;
}> = ({ anchorRef, open, setOpen, setParentDate }) => {
	const [tempDate, setTempDate] = useState<Dayjs | null>(null);

	useEffect(() => {
		if (open && !tempDate) {
			setTempDate(dayjs());
		}
	}, [open]);

	const handleAccept = () => {
		setParentDate(tempDate);
		setOpen(false);
	};

	const handleCancel = () => {
		setOpen(false);
	};

	if (!open) return null;

	return (
		<Popper
			open={open}
			anchorEl={anchorRef.current}
			placement="top-start"
			modifiers={[
				{
					name: "offset",
					options: {
						offset: [-120, 10],
					},
				},
			]}
			sx={{ zIndex: 9999 }}
		>
			<ClickAwayListener onClickAway={handleCancel}>
				<Paper
					sx={{
						backgroundColor: "#1d1d23",
						color: "#ffffff",
						borderRadius: 0,
						overflow: "hidden",
					}}
				>
					<DateCalendar
						value={tempDate}
						onChange={(newValue) => setTempDate(newValue)}
						minDate={dayjs()}
						maxDate={dayjs().add(2, "month").endOf("month")}
						views={["day"]}
						slots={{
							leftArrowIcon: LeftArrowIcon,
							rightArrowIcon: RightArrowIcon,
						}}
						showDaysOutsideCurrentMonth
						dayOfWeekFormatter={(date) => {
							const days = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];
							return days[date.day()];
						}}
						sx={{
							height: "auto",
							"& .MuiPickersDay-root": {
								color: "#ffffff",
								fontSize: "18px",
								borderRadius: 0,
							},
							"& .MuiPickersDay-root.Mui-selected": {
								backgroundColor: "#3d3d47",
							},
							"& .MuiPickersDay-root.Mui-disabled:not(.Mui-selected)": {
								color: "#696969",
							},
							"& .MuiPickersDay-root:hover": {
								backgroundColor: "rgba(255, 255, 255, 0.1)",
							},
							"& .MuiPickersDay-root.MuiPickersDay-dayOutsideMonth": {
								color: "#666666",
							},
							"& .MuiPickersDay-root.MuiPickersDay-today": {
								borderColor: "#666666",
							},
							"& .MuiDayCalendar-weekDayLabel": {
								color: "#969696",
								fontSize: "16px",
							},
							"& .MuiPickersCalendarHeader-label": {
								color: "#ffffff",
								fontSize: "18px",
								fontWeight: "bold",
							},
							"& .MuiPickersArrowSwitcher-button": {
								color: "#ffffff",
								"&.Mui-disabled": {
									color: "#444444",
									opacity: 0.5,
								},
							},
							"& .MuiPickersCalendarHeader-root": {
								display: "flex",
								justifyContent: "space-between",
								padding: "8px 16px",
								"& .MuiPickersCalendarHeader-labelContainer": {
									position: "absolute",
									left: "50%",
									transform: "translateX(-50%)",
									margin: 0,
								},
								"& button": {
									color: "#ffffff",
								},

								"& .MuiPickersArrowSwitcher-root": {
									width: "100%",
									justifyContent: "space-between",
								},
							},
							"& .MuiDayCalendar-slideTransition": {
								minHeight: "190px",
							},
							"& .MuiPickersFadeTransitionGroup-root": {
								height: "auto",
							},
						}}
					/>
					<CustomActionBar onAccept={handleAccept} onCancel={handleCancel} />
				</Paper>
			</ClickAwayListener>
		</Popper>
	);
};

const CustomActionBar: React.FC<{
	onAccept: () => void;
	onCancel: () => void;
}> = ({ onAccept, onCancel }) => {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				padding: "16px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					marginBottom: "16px",
					color: "white",
					fontSize: "14px",
					gap: "8px",
				}}
			>
				<span>לא יהיה ניתן לעדכן לאחר בחירת התאריך</span>
				<SvgIcon component={AttentionIcon} className="attention-icon" />
			</Box>
			<Box sx={{ display: "flex", gap: "12px" }}>
				<Button
					onClick={onAccept}
					sx={{
						flex: 1,
						height: "40px",
						backgroundColor: "#ffffff",
						color: "#1d1d23",
						borderRadius: 0.5,
						padding: "12px",
						fontSize: "16px",
						fontWeight: "bold",
						textTransform: "none",

						"&:hover": {
							backgroundColor: "#e0e0e0",
						},
					}}
				>
					בחר
				</Button>
				<Button
					onClick={onCancel}
					sx={{
						flex: 1,
						height: "40px",
						backgroundColor: "transparent",
						color: "#ffffff",
						border: "1px solid #ffffff",
						borderRadius: 0.5,
						padding: "12px",
						fontSize: "16px",
						fontWeight: "bold",
						textTransform: "none",
						"&:hover": {
							backgroundColor: "rgba(255, 255, 255, 0.1)",
						},
					}}
				>
					ביטול
				</Button>
			</Box>
		</Box>
	);
};
